<template>
  <nav class="mobile-bottom-nav" aria-label="Bottom navigation">
    <button
      v-for="item in navItems"
      :key="item.key"
      type="button"
      class="mobile-bottom-nav__item"
      :class="{ 'mobile-bottom-nav__item--active': isActive(item) }"
      @click="go(item.path)"
    >
      <span class="mobile-bottom-nav__item-content">
        <i :class="isActive(item) ? item.iconActive : item.icon" aria-hidden="true"></i>
        <span class="mobile-bottom-nav__label">{{ $t(item.label) }}</span>
      </span>
    </button>
  </nav>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  CART,
  FAVOURITE,
  HOME,
  INFORM,
  ORDERS,
  PRODUCT_DETAIL,
  PRODUCTS,
  SIGN_IN,
} from '@/const/path'

interface NavItem {
  key: string
  path: string
  label: string
  icon: string
  iconActive: string
  match: (path: string) => boolean
}

const router = useRouter()
const route = useRoute()

const navItems: NavItem[] = [
  {
    key: 'home',
    path: HOME,
    label: 'menu.home',
    icon: 'fa-solid fa-house',
    iconActive: 'fa-solid fa-house',
    match: (path) => path === HOME || path === '',
  },
  {
    key: 'products',
    path: PRODUCTS,
    label: 'menu.products',
    icon: 'fa-solid fa-store',
    iconActive: 'fa-solid fa-store',
    match: (path) =>
      path.startsWith(PRODUCTS) || path.startsWith(PRODUCT_DETAIL),
  },
  {
    key: 'cart',
    path: CART,
    label: 'menu.cart',
    icon: 'fa-solid fa-cart-shopping',
    iconActive: 'fa-solid fa-cart-shopping',
    match: (path) => path.startsWith(CART) || path.startsWith(ORDERS),
  },
  {
    key: 'favourite',
    path: FAVOURITE,
    label: 'text.favourite',
    icon: 'fa-regular fa-heart',
    iconActive: 'fa-solid fa-heart',
    match: (path) => path.startsWith(FAVOURITE),
  },
  {
    key: 'user',
    path: INFORM,
    label: 'menu.user',
    icon: 'fa-regular fa-user',
    iconActive: 'fa-solid fa-user',
    match: (path) =>
      path.startsWith(INFORM) ||
      path.startsWith(SIGN_IN) ||
      path.startsWith('/sign-up'),
  },
]

const currentPath = computed(() => route.path || HOME)

const isActive = (item: NavItem) => item.match(currentPath.value)

const go = (path: string) => {
  if (route.path === path) return
  router.push({ path })
}
</script>

<style scoped>
@media (min-width: 677px) {
  .mobile-bottom-nav {
    display: none !important;
  }
}

.mobile-bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: stretch;
  height: 58px;
  padding: 0 4px;
  padding-bottom: env(safe-area-inset-bottom, 0);
  box-shadow: 0 -4px 16px rgba(16, 37, 66, 0.18);
  background: #102542;
}

.mobile-bottom-nav__item {
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.mobile-bottom-nav__item-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 100%;
  padding: 6px 2px;
  color: rgba(212, 175, 55, 0.55);
  transition: color 0.2s ease, transform 0.2s ease;
}

.mobile-bottom-nav__item-content i {
  display: block;
  width: 1.25em;
  height: 1.25em;
  font-size: 18px;
  line-height: 1.25em;
  text-align: center;
}

.mobile-bottom-nav__label {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0.01em;
}

.mobile-bottom-nav__item:active .mobile-bottom-nav__item-content {
  transform: scale(0.96);
}

.mobile-bottom-nav__item--active .mobile-bottom-nav__item-content {
  color: #f4d03f;
}

.mobile-bottom-nav__item--active .mobile-bottom-nav__label {
  font-weight: 700;
}

.mobile-bottom-nav__item--active .mobile-bottom-nav__item-content i {
  transform: translateY(-1px);
}
</style>
